import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, Clock } from "lucide-react";
import { useLang } from "../i18n";

export default function Hero({ content }) {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const title = lang === "en" ? content.hero_title_en : content.hero_title_es;
  const subtitle = lang === "en" ? content.hero_subtitle_en : content.hero_subtitle_es;
  const words = title.split(" ");

  const container = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } } };
  const wordVar = {
    hidden: { y: "110%", opacity: 0 },
    show: { y: "0%", opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY, scale: bgScale }}>
        <img src={content.hero_bg} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-28 pb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative mb-10 w-fit"
        >
          <div className="absolute -inset-8 sm:-inset-12 rounded-full bg-[#E10600]/25 blur-3xl -z-10" aria-hidden="true" />
          <motion.img
            src={content.logo_url}
            alt={content.business_name}
            animate={{ scale: [1, 1.03, 1] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            style={{
              WebkitMaskImage: "radial-gradient(ellipse 82% 82% at 50% 50%, #000 68%, transparent 100%)",
              maskImage: "radial-gradient(ellipse 82% 82% at 50% 50%, #000 68%, transparent 100%)",
            }}
            className="h-56 sm:h-80 lg:h-[26rem] w-auto object-contain drop-shadow-[0_20px_70px_rgba(225,6,0,0.6)]"
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary-red mb-5"
        >
          {lang === "en" ? content.tagline_en : content.tagline_es} · Newark, NJ
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.92] max-w-4xl"
          data-testid="hero-title"
        >
          {words.map((w, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.25em]">
              <motion.span variants={wordVar} className="inline-block">
                {w}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-7 max-w-xl font-body text-lg text-neutral-300 leading-relaxed"
        >
          {subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={`tel:${content.phone.replace(/[^0-9]/g, "")}`}
            className="group flex items-center gap-3 bg-primary-red text-white font-body font-bold uppercase tracking-wide px-7 py-4 hover:bg-[#FF1E16] transition-colors"
            data-testid="hero-call"
          >
            <Phone size={18} />
            {t.hero.cta_call}
          </a>
          <button
            onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
            className="flex items-center gap-2 border border-white/25 text-white font-body font-bold uppercase tracking-wide px-7 py-4 hover:border-primary-red hover:text-primary-red transition-colors"
            data-testid="hero-services"
          >
            {t.hero.cta_services}
          </button>
        </motion.div>
      </motion.div>

      {/* 24/7 floating badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 120 }}
        className="hidden md:flex absolute right-8 lg:right-16 top-1/2 -translate-y-1/2 z-10 flex-col items-center justify-center w-40 h-40 rounded-full bg-primary-red text-white pulse-ring"
        data-testid="badge-247"
      >
        <Clock size={26} className="mb-1" />
        <span className="font-display text-4xl leading-none">24/7</span>
        <span className="font-body font-bold uppercase text-xs tracking-widest mt-1 text-center px-2">{t.hero.badge}</span>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-neutral-400"
      >
        <span className="font-mono2 text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.div>
    </section>
  );
}
